import React, { useEffect, useRef, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';

export const HomeCoreFeaturesStack: React.FC = () => {
   const { t, lang } = useLanguage();
   const sectionRef = useRef<HTMLElement>(null);
   const [isVisible, setIsVisible] = useState(false);

   useEffect(() => {
      const observer = new IntersectionObserver(
         ([entry]) => {
            if (entry.isIntersecting) {
               setIsVisible(true);
               observer.unobserve(entry.target);
            }
         },
         { threshold: 0.1 }
      );

      if (sectionRef.current) {
         observer.observe(sectionRef.current);
      }

      return () => observer.disconnect();
   }, []);

   return (
      <section ref={sectionRef} className="bg-transparent pt-40 pb-10 relative overflow-hidden">
         {/* Background Glow */}
         <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[800px] bg-blue-900/10 blur-[150px] rounded-full pointer-events-none" />

         <div className="max-w-7xl mx-auto px-6 relative z-10 text-center">
            <h2 className={`text-4xl md:text-5xl font-bold text-white mb-28 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
               {t('stack.title')}
            </h2>

            <div className="relative h-[1100px] md:h-[800px] w-full max-w-5xl mx-auto perspective-1000">

               {/* Card 1: Top Center */}
               <div
                  className={`md:absolute md:top-0 md:left-1/2 md:-translate-x-1/2 bg-white text-black rounded-3xl shadow-2xl w-full md:w-[340px] overflow-hidden transition-all duration-1000 delay-300 z-20 ${isVisible ? 'opacity-100 translate-y-0 rotate-0' : 'opacity-0 translate-y-40 rotate-12'}`}
                  style={{ willChange: 'transform, opacity' }}
               >
                  <div className="p-8 text-left">
                     <div className="w-14 h-14 bg-[#163B82] rounded-2xl flex items-center justify-center text-3xl mb-6 shadow-lg text-white">
                        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                           <circle cx="12" cy="12" r="3" stroke="white" strokeWidth="2" fill="none" />
                        </svg>
                     </div>
                     <h3 className="text-2xl font-bold mb-3 leading-tight">
                        {t('stack.item1.title')} <br />
                        {lang === 'zh' && <span className="text-xl text-gray-800">{t('stack.item1.subtitle')}</span>}
                     </h3>
                     <p className="text-gray-500 text-sm leading-relaxed mb-6 font-medium">
                        {t('stack.item1.desc')}
                     </p>
                     <div className="text-sm text-blue-700 font-bold tracking-tight">#RiskAnalysis #Compliance</div>
                  </div>
               </div>

               {/* Card 2: Middle Left */}
               <div
                  className={`md:absolute md:top-[220px] md:left-24 bg-white text-black rounded-3xl shadow-2xl w-full md:w-[340px] overflow-hidden transition-all duration-1000 delay-500 z-10 ${isVisible ? 'opacity-100 translate-x-0 rotate-0' : 'opacity-0 -translate-x-40 -rotate-12'}`}
                  style={{ willChange: 'transform, opacity' }}
               >
                  <div className="p-8 text-left">
                     <div className="w-14 h-14 bg-[#163B82] rounded-2xl flex items-center justify-center text-3xl mb-6 shadow-lg text-white">
                        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                     </div>
                     <h3 className="text-2xl font-bold mb-3 leading-tight">
                        {t('stack.item2.title')} <br />
                        {lang === 'zh' && <span className="text-xl text-gray-800">{t('stack.item2.subtitle')}</span>}
                     </h3>
                     <p className="text-gray-500 text-sm leading-relaxed mb-6 font-medium">
                        {t('stack.item2.desc')}
                     </p>
                     <div className="text-sm text-blue-700 font-bold tracking-tight">#ProcessViz #Workflow</div>
                  </div>
               </div>

               {/* Card 3: Bottom Right */}
               <div
                  className={`md:absolute md:top-[260px] md:right-24 bg-white text-black rounded-3xl shadow-2xl w-full md:w-[340px] overflow-hidden transition-all duration-1000 delay-700 z-30 ${isVisible ? 'opacity-100 translate-x-0 rotate-0' : 'opacity-0 translate-x-40 rotate-12'}`}
                  style={{ willChange: 'transform, opacity' }}
               >
                  <div className="p-8 text-left">
                     <div className="w-14 h-14 bg-[#163B82] rounded-2xl flex items-center justify-center text-3xl mb-6 shadow-lg text-white">
                        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                        </svg>
                     </div>
                     <h3 className="text-2xl font-bold mb-3 leading-tight">
                        {t('stack.item3.title')} <br />
                        {lang === 'zh' && <span className="text-xl text-gray-800">{t('stack.item3.subtitle')}</span>}
                     </h3>
                     <p className="text-gray-500 text-sm leading-relaxed mb-6 font-medium">
                        {t('stack.item3.desc')}
                     </p>
                     <div className="text-sm text-blue-700 font-bold tracking-tight">#ExportDocs #Collaboration</div>
                  </div>
               </div>
            </div>
         </div>
         <style>{`
            .perspective-1000 {
                perspective: 1000px;
            }
         `}</style>
      </section>
   );
};
